import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Kitchen, ListQualityMetricsQuery, ListQualityMetricsQueryVariables, QualityMetric, Session } from '../libs';
import { getDaysInRange, promisePoolForTasks, splitDateRangeByDays } from './utils';
import { GraphQLClient, LIST_QUALITY_METRICS_QUERY } from '../libs/graphQL';

interface Props {
  startDate: string;
  endDate: string;
  kitchen: Kitchen;
  shift?: Session;
}

const useListQualityMetricsQuery = ({ startDate, endDate, kitchen, shift }: Props) => {
  const [qualityMetrics, setQualityMetrics] = useState<QualityMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const daysInRange = getDaysInRange(startDate, endDate);

  const loadingDays = useMemo(() => {
    if (!loading) return [];
    const fetchedDays = new Set(qualityMetrics.map((metric) => metric.day));
    return daysInRange.filter((day) => !fetchedDays.has(day));
  }, [loading, daysInRange, qualityMetrics]);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      setQualityMetrics([]);

      const intervals = splitDateRangeByDays(startDate, endDate, 2);
      const tasks = intervals.map(({ start, end }) => async () => {
        try {
          const { data } = await GraphQLClient.query<ListQualityMetricsQuery, ListQualityMetricsQueryVariables>({
            query: LIST_QUALITY_METRICS_QUERY,
            variables: { startDate: start, endDate: end, kitchen, shift },
            fetchPolicy: 'network-only'
          });
          const chunkData: QualityMetric[] = data?.listQualityMetrics?.data ?? [];
          if (!cancelled) {
            setQualityMetrics((prev) => [...prev, ...chunkData]);
          }
          return chunkData;
        } catch (error) {
          toast.error(
            `Error fetching Quality Metrics for ${start} - ${end}: ${error instanceof Error ? error.message : String(error)}`
          );
          return [];
        }
      });

      await promisePoolForTasks(tasks, 5);
      if (!cancelled) setLoading(false);
    };

    void fetchAll();

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, kitchen, shift]);

  const clearData = () => {
    setQualityMetrics([]);
    void GraphQLClient.clearStore();
  };

  return {
    qualityMetricsLoading: loading,
    qualityMetrics,
    loadingDays,
    daysInRange,
    clearQualityMetricsData: clearData
  };
};

export default useListQualityMetricsQuery;
